import React, { useEffect, useMemo, useState, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Calendar,
  Home,
  Music,
  HandCoins,
  BookOpenText,
  BookOpen,
  Mic2,
  Users,
  Handshake,
  Bell,
  MessageCircle,
  Send,
  CheckCircle2,
  HeartHandshake,
  Church,
  Settings,
  BarChart3,
  Star,
  Heart,
  Eye,
  EyeOff,
  Zap,
  Award,
  Crown,
  Sparkles,
  Sun,
  Moon,
  Camera,
  Image as ImageIcon,
  Upload,
  Lock,
  LogOut,
  User,
  Shield,
  Mail,
  X,
  Trash2,
  Edit,
  MapPin,
  Clock,
  PoundSterling,
  Paperclip,
  UserCog,
  FileText,
  Plus,
  Download,
  Filter,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  Video,
  Wheat,
  Radio,
  GripVertical,
  Check,
  MoreHorizontal,
  MonitorPlay,
  Building2,
  Megaphone,
  DoorOpen,
  GraduationCap,
  ExternalLink,
  Phone,
  ChevronDown
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import GPSAttendancePage from "./GPSAttendancePage";
import LiveAnnouncementsTicker from "./components/LiveAnnouncementsTicker";
import AdminPanel from "./components/AdminPanel";
import TabBar from "./components/layout/TabBar";
import { 
  IncomeSection, 
  AttachmentsSection, 
  AdminUsersSection, 
  AuditLogsSection,
  FinanceSection,
  AnnouncementsSection,
  ContactMessagesSection,
  WebcastSection,
  MinistryEnrollmentsSection,
  FlyersSection,
  ShepherdCornerSection,
  HarvestSection,
  ServiceScheduleSection,
  ChoirMinistrySection,
  EmailComposeSection,
  MediaDepartmentSection,
  SecretarialAdminSection,
  MembersSection,
  SettingsSection,
  CommunicationsDepartmentSection,
  UsherDepartmentSection,
  SundaySchoolDepartmentSection,
  ManualAttendanceEntry
} from "./components/admin";
import { 
  EventsPage, 
  SermonsPage, 
  PrayerPage, 
  GivingPage, 
  TeamsPage,
  ChoirPage,
  FancyButton,
  SectionCard,
  LoginPage
} from "./pages";
import MeetOurTeamPage from "./pages/MeetOurTeamPage";
import ChoirMemberPortal from "./pages/ChoirMemberPortal";
import RulesPresentation from "./pages/RulesPresentation";
import LiveDisplay from "./pages/LiveDisplay";
import RemoteControl from "./pages/RemoteControl";
import PresentationRemote from "./pages/PresentationRemote";
import VolunteerClockIn from "./pages/VolunteerClockIn";
import MemberRegister from "./pages/MemberRegister";
import MobileHarvestRecord from "./pages/MobileHarvestRecord";
// Removed: /join-choir public page and JoinChoir page import — choir intake is admin-only now.
import ChoirCheckIn from "./pages/ChoirCheckIn";
import MemberLogin from "./pages/MemberLogin";
import MinisterAvailability from "./pages/MinisterAvailability";
import FlyerDetailPage from "./pages/FlyerDetailPage";
import MediaDetailPage from "./pages/MediaDetailPage";
import { LongPressShare } from "./components/ShareButton";
import { AppProvider } from "./context/AppContext";
import SeasonalInspiration from "./components/SeasonalInspiration";
import { useLocalState, clearOldCache, CACHE_VERSION, CACHE_TTL } from "./hooks";
import BibleVerseModal from "./components/home/BibleVerseModal";
import FlyerLightboxModal from "./components/home/FlyerLightboxModal";
import AboutUsModal from "./components/home/AboutUsModal";
import MinistryEnrollmentModal from "./components/home/MinistryEnrollmentModal";
import MinistryEnrollmentsAdminModal from "./components/home/MinistryEnrollmentsAdminModal";
import ConnectWithUsSection from "./components/home/ConnectWithUsSection";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = `${BACKEND_URL}/api`;

// Clear old cache on load
clearOldCache();

const AppShell = ({ title, children, church, onAboutClick }) => {
  const [isDark, setIsDark] = useState(false);
  
  return (
    <div className={`min-h-screen transition-all duration-500 ${
      isDark 
        ? 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white' 
        : 'bg-gradient-to-br from-amber-50 via-violet-50 to-amber-50 text-slate-900'
    }`}>
      {/* Fixed Header - Always visible */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-gradient-to-r from-violet-900/95 via-purple-800/95 to-violet-900/95 border-b-2 border-amber-400/60 shadow-xl">
        <div className="max-w-[1600px] mx-auto px-4 py-3">
          {/* Main Header Row */}
          <div className="flex items-center gap-4">
            <motion.div 
              className="flex-shrink-0 relative"
              whileHover={{ scale: 1.1, rotate: 360 }}
              transition={{ duration: 0.6 }}
            >
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full blur opacity-50 animate-pulse"></div>
              <img 
                src="/images/church-logo.png?v=2" 
                alt="CCC VOB PARISH Logo" 
                className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-amber-300 shadow-2xl"
              />
            </motion.div>
            
            <div className="flex-1 min-w-0">
              <motion.h1 
                className="font-bold text-lg sm:text-xl md:text-2xl leading-tight"
                style={{ 
                  background: 'linear-gradient(135deg, #FDE68A 0%, #FBBF24 25%, #FFFFFF 50%, #FBBF24 75%, #FDE68A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                {title}
              </motion.h1>
              {/* Address Row */}
              <motion.a 
                href={church?.google_maps_url || `https://www.google.com/maps?q=51.4449948,0.213802`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-amber-100 flex items-center gap-1.5 mt-0.5 hover:text-amber-300 transition-colors group"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <MapPin className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                <span className="hover:underline">{church?.address || '62 Spital Street, Dartford DA1 2DT'}</span>
                <ExternalLink className="w-3 h-3 text-amber-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.a>
            </div>
            
            <div className="flex items-center gap-2">
              {/* About Us Button */}
              <motion.button
                onClick={onAboutClick}
                className="p-2 rounded-xl bg-violet-700 hover:bg-violet-600 text-white shadow-md hover:shadow-lg transition-all border border-amber-400/50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="About Us"
              >
                <Church className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
              
              {/* Meet Our Team Button */}
              <Link to="/meet-the-team">
                <motion.button
                  className="p-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-violet-900 shadow-md hover:shadow-lg transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title="Meet Our Team"
                  data-testid="header-meet-team-btn"
                >
                  <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                </motion.button>
              </Link>
              
              {/* Dark Mode Toggle */}
              <motion.button
                onClick={() => setIsDark(!isDark)}
                className="p-2 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 text-violet-900 shadow-md hover:shadow-lg transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isDark ? <Sun className="w-4 h-4 sm:w-5 sm:h-5" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5" />}
              </motion.button>
              
              {/* Live Updates Badge */}
              <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 text-white text-sm font-medium shadow-md">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <Bell className="w-4 h-4" />
                </motion.div>
                <span>Live</span>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Spacer for fixed header + top TabBar
          - mobile: header (church name wraps 4 lines) ~160px + tabbar ~56px = ~220px
          - desktop: header ~96px + tabbar ~58px = ~154px */}
      <div className="h-[220px] sm:h-[156px]"></div>
      
      <main className="max-w-[1600px] mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 pb-8">
        {children}
      </main>
      
      <footer className="max-w-[1600px] mx-auto px-4 pb-8 text-center">
        <motion.div 
          className="flex items-center justify-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <span>Built with</span>
          <motion.div
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <Heart className="w-4 h-4 text-red-500" />
          </motion.div>
          <span>for the Kingdom</span>
          <Crown className="w-4 h-4 text-yellow-500" />
        </motion.div>
      </footer>
    </div>
  );
};

// NavButton and TabBar are now imported from ./components/TabBar.js
// LoginPage is now imported from ./pages/LoginPage.js

function ChurchAppContent() {
  const [tab, setTab] = useState("home");
  const [showAboutModal, setShowAboutModal] = useState(false);
  
  const [church, setChurch] = useLocalState("church.meta", {
    name: "Celestial Church of Christ — Valley of Berachah (VOB) Parish",
    address: "62 Spital Street, Dartford DA1 2DT",
    verseOfDay: "The Lord is my shepherd; I shall not want. — Psalm 23:1",
    givingUrl: "https://example.com/ccc-vob-giving",
    serviceTimes: {
      sunday: "10:00",
      wedMorning: "09:00",
      wedEvening: "19:00",
      fridayEvening: "19:00",
      newMoon: "22:00"
    }
  });

  const [announcements, setAnnouncements] = useLocalState("church.ann", []);
  const [events, setEvents] = useLocalState("church.events", []);
  const [sermons, setSermons] = useLocalState("church.sermons", []);
  const [prayers, setPrayers] = useLocalState("church.prayers", []);
  const [teams, setTeams] = useLocalState("church.teams", []);
  const [attendance, setAttendance] = useLocalState("church.attendance", []);
  const [rehearsals, setRehearsals] = useLocalState("choir.rehearsals", []);
  const [setlist, setSetlist] = useLocalState("choir.setlist", []);
  const [mediaItems, setMediaItems] = useLocalState("church.media", [
    {
      id: "1",
      title: "Sunday Service Worship",
      description: "Beautiful moments from our Sunday morning worship service",
      url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=800&fit=crop",
      uploadDate: "2025-10-01",
      type: "image"
    },
    {
      id: "2", 
      title: "Church Building",
      description: "Our beautiful church sanctuary where we gather to worship",
      url: "https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=800&h=800&fit=crop",
      uploadDate: "2025-09-28",
      type: "image"
    },
    {
      id: "3",
      title: "Community Outreach",
      description: "Serving our community with love and compassion",
      url: "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&h=800&fit=crop",
      uploadDate: "2025-09-25",
      type: "image"
    }
  ]);
  const [serviceSchedules, setServiceSchedules] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [flyers, setFlyers] = useState([]);


  // Enhanced Authentication System
  const [isAuthenticated, setIsAuthenticated] = useLocalState("church.authenticated", false);
  const [adminUser, setAdminUser] = useLocalState("church.adminUser", null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  
  // Sync auth state from localStorage on mount (handles login redirect)
  useEffect(() => {
    try {
      const authKey = `${CACHE_VERSION}.church.authenticated`;
      const userKey = `${CACHE_VERSION}.church.adminUser`;
      const authData = localStorage.getItem(authKey);
      const userData = localStorage.getItem(userKey);
      
      if (authData) {
        const { data, timestamp } = JSON.parse(authData);
        if (data === true && timestamp && Date.now() - timestamp < CACHE_TTL) {
          setIsAuthenticated(true);
        }
      }
      if (userData) {
        const { data, timestamp } = JSON.parse(userData);
        if (data && timestamp && Date.now() - timestamp < CACHE_TTL) {
          setAdminUser(data);
        }
      }
    } catch (e) {
      console.error("Error syncing auth state:", e);
    }
  }, []);
  
  // Member Registration State (Optional)
  const [memberId, setMemberId] = useLocalState("attendance.member_id", null);
  const [registrationData, setRegistrationData] = useState({ full_name: "", phone: "", email: "", birthday_month: "", birthday_day: "", gender: "" });
  const [registrationError, setRegistrationError] = useState("");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showGPSModal, setShowGPSModal] = useState(false);
  const [gpsPermissionStatus, setGpsPermissionStatus] = useState(null); // 'granted', 'denied', 'pending'

  const [loginError, setLoginError] = useState("");

  // Load data from API on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      // Load church settings
      const churchRes = await axios.get(`${API}/church`);
      if (churchRes.data) {
        const churchData = churchRes.data;
        setChurch({
          name: churchData.name,
          address: churchData.address || "",
          verseOfDay: churchData.verse_of_day,
          givingUrl: churchData.giving_url,
          serviceTimes: churchData.service_times
        });
      }

      // Load all other data independently — each call wrapped so one failure doesn't block others
      const loads = [
        axios.get(`${API}/announcements/enhanced`, { params: { include_all: false } }).then(r => {
          const published = r.data.filter(a => a.status === 'published');
          setAnnouncements(published);
        }),
        axios.get(`${API}/events`).then(r => setEvents(r.data)),
        axios.get(`${API}/sermons`).then(r => setSermons(r.data)),
        axios.get(`${API}/prayers`).then(r => setPrayers(r.data)),
        axios.get(`${API}/teams`).then(r => setTeams(r.data)),
        axios.get(`${API}/attendance`).then(r => setAttendance(r.data)),
        axios.get(`${API}/choir/rehearsals`).then(r => setRehearsals(r.data)),
        axios.get(`${API}/choir/songs`).then(r => setSetlist(r.data)),
        axios.get(`${API}/service-schedule`).then(r => setServiceSchedules(r.data)),
        axios.get(`${API}/ministries`).then(r => setMinistries(r.data)),
        axios.get(`${API}/flyers`).then(r => setFlyers(r.data)),
      ];
      const results = await Promise.allSettled(loads);
      results.forEach((r, i) => { if (r.status === 'rejected') console.error(`Data load #${i} failed:`, r.reason?.message); });
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  // Authentication functions
  const handleLogin = () => {
    if (loginCredentials.username.toUpperCase() === "SEMILORE" && loginCredentials.password.toUpperCase() === "SEMILORE") {
      setIsAuthenticated(true);
      setShowLogin(false);
      setLoginError("");
      setLoginCredentials({ username: "", password: "" });
    } else {
      setLoginError("Invalid username or password. Please try again.");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminUser(null);
    // Clear localStorage to ensure clean logout
    localStorage.removeItem(`${CACHE_VERSION}.church.authenticated`);
    localStorage.removeItem(`${CACHE_VERSION}.church.adminUser`);
    setTab("home"); // Redirect to home after logout
  };

  // Refresh announcements for live ticker
  const refreshAnnouncements = useCallback(async () => {
    try {
      const annRes = await axios.get(`${API}/announcements/enhanced`, { params: { include_all: false } });
      const publishedAnn = annRes.data.filter(a => a.status === 'published');
      setAnnouncements(publishedAnn);
    } catch (error) {
      console.error("Error refreshing announcements:", error);
    }
  }, [setAnnouncements]);

  // Registration function
  const handleRegistration = async () => {
    if (!registrationData.full_name.trim()) { setRegistrationError("Full Name is required"); return; }
    if (!registrationData.phone.trim()) { setRegistrationError("Phone Number is required"); return; }
    // UK phone validation
    const phoneClean = registrationData.phone.trim().replace(/[\s\-()]/g, '');
    if (phoneClean.startsWith('+44')) {
      if (phoneClean.length !== 13 || !/^\+44\d{10}$/.test(phoneClean)) {
        setRegistrationError("Invalid UK phone. Must be 11 digits (e.g. 07XXX XXXXXX) or +44 followed by 10 digits"); return;
      }
    } else if (phoneClean.startsWith('0')) {
      if (phoneClean.length !== 11 || !/^0\d{10}$/.test(phoneClean)) {
        setRegistrationError("Invalid UK phone. Must be 11 digits (e.g. 07XXX XXXXXX)"); return;
      }
    } else {
      setRegistrationError("Invalid UK phone. Must start with 0 or +44"); return;
    }
    if (!registrationData.email.trim()) { setRegistrationError("Email is required"); return; }
    if (!registrationData.birthday_month || !registrationData.birthday_day) { setRegistrationError("Birthday (month and day) is required"); return; }
    if (!registrationData.gender) { setRegistrationError("Please select your gender"); return; }
    
    try {
      const birthday = `${registrationData.birthday_month} ${registrationData.birthday_day}`;
      const response = await axios.post(`${API}/members/register`, {
        full_name: registrationData.full_name.trim(),
        phone: registrationData.phone.trim(),
        email: registrationData.email.trim(),
        birthday,
        gender: registrationData.gender,
        source: "homepage"
      });
      
      const newMemberId = response.data.member_id;
      setMemberId(newMemberId);
      localStorage.setItem("attendance.member_id", newMemberId);
      setRegistrationError("");
      setRegistrationSuccess(true);
      setRegistrationData({ full_name: "", phone: "", email: "", birthday_month: "", birthday_day: "", gender: "" });
    } catch (error) {
      console.error("Registration error:", error);
      setRegistrationError(error.response?.data?.detail || "Registration failed. Please try again.");
    }
  };

  // Request GPS permission
  const requestGPSPermission = () => {
    if ('geolocation' in navigator) {
      setGpsPermissionStatus('requesting');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log("GPS permission granted", position);
          setGpsPermissionStatus('granted');
          localStorage.setItem("gps_permission_granted", "true");
        },
        (error) => {
          console.error("GPS permission denied:", error);
          setGpsPermissionStatus('denied');
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setGpsPermissionStatus('unsupported');
    }
  };


  const page = useMemo(() => {
    switch (tab) {
      case "home":
        return (
          <HomePage
            church={church}
            announcements={announcements}
            isAdmin={isAuthenticated}
            setAnnouncements={setAnnouncements}
            setChurch={setChurch}
            serviceSchedules={serviceSchedules}
            setServiceSchedules={setServiceSchedules}
            ministries={ministries}
            setMinistries={setMinistries}
            memberId={memberId}
            registrationData={registrationData}
            setRegistrationData={setRegistrationData}
            registrationError={registrationError}
            registrationSuccess={registrationSuccess}
            handleRegistration={handleRegistration}
            refreshAnnouncements={refreshAnnouncements}
            flyers={flyers}
            setFlyers={setFlyers}
            showAboutModal={showAboutModal}
            setShowAboutModal={setShowAboutModal}
          />
        );
      case "events":
        return (
          <EventsPage events={events} setEvents={setEvents} isAdmin={isAuthenticated} church={church} />
        );
      case "sermons":
        return (
          <SermonsPage sermons={sermons} setSermons={setSermons} isAdmin={isAuthenticated} />
        );
      case "prayer":
        return (
          <PrayerPage prayers={prayers} setPrayers={setPrayers} isAuthenticated={isAuthenticated} />
        );
      case "admin":
        // Admin Panel - Backend management (only accessible when authenticated)
        if (!isAuthenticated) return null;
        return (
          <AdminPanel 
            isAuthenticated={isAuthenticated} 
            memberId={memberId}
            church={church}
            setChurch={setChurch}
            adminUser={adminUser}
            serviceSchedules={serviceSchedules}
            setServiceSchedules={setServiceSchedules}
          />
        );
      case "webcast":
        return <WebcastPage />;
      case "give":
        return <GivingPage givingUrl={church.givingUrl} setChurch={setChurch} isAdmin={isAuthenticated} />;
      case "choir":
        return <ChoirPage rehearsals={rehearsals} setRehearsals={setRehearsals} setlist={setlist} setSetlist={setSetlist} isAuthenticated={isAuthenticated} />;
      case "teams":
        return <TeamsPage teams={teams} setTeams={setTeams} isAdmin={isAuthenticated} />;
      default:
        return null;
    }
  }, [tab, church, announcements, isAuthenticated, events, sermons, prayers, teams, rehearsals, setlist, serviceSchedules, ministries, memberId, registrationData, registrationError, registrationSuccess, flyers, showAboutModal]);

  return (
    <AppShell title={church.name} church={church} onAboutClick={() => setShowAboutModal(true)}>
      {isAuthenticated && (
        <motion.div 
          className="flex items-center justify-end gap-3 mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div
            className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-medium shadow-lg"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", bounce: 0.5 }}
          >
            <Crown className="w-4 h-4" />
            <span>Admin: {adminUser?.full_name || 'Unknown'}</span>
          </motion.div>
          
          <motion.button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-300 shadow-lg bg-gradient-to-r from-red-500 to-pink-500 text-white hover:from-red-600 hover:to-pink-600"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </motion.button>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, x: 300, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: -300, scale: 0.8 }}
          transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
          className="space-y-6"
        >
          {page}
        </motion.div>
      </AnimatePresence>

      {/* GPS Permission Modal */}
      <AnimatePresence>
        {showGPSModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
            >
              {gpsPermissionStatus === 'pending' || gpsPermissionStatus === 'requesting' ? (
                <>
                  <div className="text-center mb-6">
                    <motion.div 
                      className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                    >
                      <MapPin className="w-12 h-12 text-white" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Enable GPS Attendance</h2>
                    <p className="text-slate-600">
                      Allow location access to automatically record your attendance when you arrive at church during service hours.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
                    <ul className="text-sm text-blue-800 space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                        <span>GPS only active on <strong>Sundays 10 AM - 6 PM</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                        <span>Automatically marks attendance when at church</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                        <span>Your location data is kept private</span>
                      </li>
                    </ul>
                  </div>
                  
                  <button
                    onClick={requestGPSPermission}
                    disabled={gpsPermissionStatus === 'requesting'}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold text-lg hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {gpsPermissionStatus === 'requesting' ? (
                      <>
                        <motion.div 
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        />
                        <span>Requesting Permission...</span>
                      </>
                    ) : (
                      <>
                        <MapPin className="w-5 h-5" />
                        <span>Allow Location Access</span>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setShowGPSModal(false)}
                    className="w-full mt-3 py-3 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                  >
                    Skip for Now
                  </button>
                </>
              ) : gpsPermissionStatus === 'granted' ? (
                <div className="text-center">
                  <motion.div 
                    className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                  >
                    <CheckCircle2 className="w-12 h-12 text-white" />
                  </motion.div>
                  <h2 className="text-2xl font-bold text-emerald-700 mb-2">GPS Enabled!</h2>
                  <p className="text-slate-600 mb-6">
                    Your attendance will now be recorded automatically when you're at church during service hours.
                  </p>
                  <button
                    onClick={() => setShowGPSModal(false)}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold text-lg hover:from-emerald-600 hover:to-green-600 transition-all shadow-lg"
                  >
                    Got it!
                  </button>
                </div>
              ) : gpsPermissionStatus === 'denied' ? (
                <div className="text-center">
                  <motion.div 
                    className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    <AlertCircle className="w-12 h-12 text-white" />
                  </motion.div>
                  <h2 className="text-2xl font-bold text-amber-700 mb-2">GPS Permission Denied</h2>
                  <p className="text-slate-600 mb-4">
                    You can still use the app, but automatic attendance tracking won't work. You can enable location access later in your browser settings.
                  </p>
                  <button
                    onClick={() => setShowGPSModal(false)}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg"
                  >
                    Continue Anyway
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h2 className="text-xl font-bold text-slate-700 mb-2">GPS Not Supported</h2>
                  <p className="text-slate-600 mb-4">Your browser doesn't support GPS. Please try a different browser.</p>
                  <button
                    onClick={() => setShowGPSModal(false)}
                    className="w-full py-3 rounded-xl bg-slate-500 text-white font-bold"
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <TabBar current={tab} setCurrent={setTab} isAuthenticated={isAuthenticated} />
    </AppShell>
  );
}

// --- Enhanced Pages ---
const MONTHS_LIST = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function HomePage({ church, announcements, isAdmin, setAnnouncements, setChurch, serviceSchedules, setServiceSchedules, ministries, setMinistries, memberId, registrationData, setRegistrationData, registrationError, registrationSuccess, handleRegistration, refreshAnnouncements, flyers, setFlyers, showAboutModal, setShowAboutModal }) {
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", subject: "", category: "general_inquiry", message: "" });
  const [contactMessages, setContactMessages] = useState([]);
  const [showMessages, setShowMessages] = useState(false);
  const [weeklyVerse, setWeeklyVerse] = useState(null);
  const [bibleLessons, setBibleLessons] = useState([]);
  const [showMemberRegistration, setShowMemberRegistration] = useState(false);
  const [weeklyBibleStudy, setWeeklyBibleStudy] = useState(null);
  
  // Service Schedule state
  const [newSchedule, setNewSchedule] = useState({ name: "", day: "", time: "", description: "" });
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  // Ministry state
  const [enrollmentForm, setEnrollmentForm] = useState({ ministry_id: "", member_name: "", member_phone: "", member_email: "" });
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [ministryEnrollments, setMinistryEnrollments] = useState([]);
  const [showEnrollmentsModal, setShowEnrollmentsModal] = useState(false);
  
  // Bible verse modal state
  const [showBibleModal, setShowBibleModal] = useState(false);
  const [bibleVerseData, setBibleVerseData] = useState(null);
  const [loadingVerse, setLoadingVerse] = useState(false);
  
  // Flyer lightbox state
  const [selectedFlyer, setSelectedFlyer] = useState(null);
  const [showFlyerModal, setShowFlyerModal] = useState(false);

  // Load weekly Bible study topic
  useEffect(() => {
    const loadWeeklyBibleStudy = async () => {
      try {
        const response = await axios.get(`${API}/bible-lessons/weekly-study`);
        if (response.data && response.data.topic) {
          setWeeklyBibleStudy(response.data);
        }
      } catch (error) {
        console.log("No weekly Bible study available");
      }
    };
    loadWeeklyBibleStudy();
  }, []);
  
  // Load weekly verse on component mount
  useEffect(() => {
    const loadWeeklyVerse = async () => {
      try {
        const response = await axios.get(`${API}/weekly-verse`);
        setWeeklyVerse(response.data);
      } catch (error) {
        console.error('Error loading weekly verse:', error);
      }
    };
    
    loadWeeklyVerse();
  }, []);

  // Load Bible lessons for current week
  useEffect(() => {
    const loadBibleLessons = async () => {
      try {
        const response = await axios.get(`${API}/bible-lessons/current-week`);
        // API returns {week_start, week_end, lessons} - extract the lessons array
        const lessons = response.data?.lessons || response.data || [];
        setBibleLessons(Array.isArray(lessons) ? lessons : []);
      } catch (error) {
        console.error('Error loading Bible lessons:', error);
        setBibleLessons([]);
      }
    };
    
    loadBibleLessons();
  }, []);

  const updateChurch = async (updates) => {
    const updatedChurch = { ...church, ...updates };
    setChurch(updatedChurch);
    
    try {
      await axios.post(`${API}/church`, {
        name: updatedChurch.name,
        address: updatedChurch.address,
        verse_of_day: updatedChurch.verseOfDay,
        giving_url: updatedChurch.givingUrl,
        service_times: updatedChurch.serviceTimes
      });
    } catch (error) {
      console.error("Error updating church settings:", error);
    }
  };

  
  const submitContactForm = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.message) {
      alert("Please fill in your name and message");
      return;
    }
    
    try {
      await axios.post(`${API}/contact-messages`, {
        full_name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        subject: contactForm.subject || 'General Inquiry',
        category: contactForm.category,
        message: contactForm.message
      });
      alert("Thank you! Your message has been sent.");
      setContactForm({ name: "", email: "", phone: "", subject: "", category: "general_inquiry", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      alert("Error sending message. Please try again.");
    }
  };
  
  const fetchContactMessages = async () => {
    if (!isAdmin) return;
    
    try {
      const response = await axios.get(`${API}/contact`);
      setContactMessages(response.data);
      setShowMessages(true);
    } catch (error) {
      console.error("Error fetching contact messages:", error);
    }
  };
  
  const markMessageRead = async (messageId) => {
    try {
      await axios.patch(`${API}/contact/${messageId}/read`);
      setContactMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, read: true } : msg
      ));
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };
  
  const deleteMessage = async (messageId) => {
    if (!window.confirm("Delete this message?")) return;
    
    try {
      await axios.delete(`${API}/contact/${messageId}`);
      setContactMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  // Service Schedule Functions
  const addServiceSchedule = async () => {
    if (!newSchedule.name || !newSchedule.day || !newSchedule.time) {
      alert("Please fill in all required fields");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/service-schedule`, newSchedule);
      setServiceSchedules(prev => [...prev, response.data]);
      setNewSchedule({ name: "", day: "", time: "", description: "" });
    } catch (error) {
      console.error("Error adding service schedule:", error);
      alert("Failed to add service schedule");
    }
  };
  
  const updateServiceSchedule = async (scheduleId, updates) => {
    try {
      await axios.put(`${API}/service-schedule/${scheduleId}`, updates);
      setServiceSchedules(prev => prev.map(s => s.id === scheduleId ? { ...s, ...updates } : s));
      setEditingSchedule(null);
    } catch (error) {
      console.error("Error updating service schedule:", error);
      alert("Failed to update service schedule");
    }
  };
  
  // Ministry Functions
  const enrollInMinistry = async () => {
    if (!enrollmentForm.ministry_id || !enrollmentForm.member_name || !enrollmentForm.member_phone) {
      alert("Please fill in all required fields");
      return;
    }
    
    try {
      await axios.post(`${API}/ministry-enrollments`, enrollmentForm);
      alert("Successfully enrolled in ministry!");
      setEnrollmentForm({ ministry_id: "", member_name: "", member_phone: "", member_email: "" });
      setShowEnrollmentModal(false);
    } catch (error) {
      console.error("Error enrolling in ministry:", error);
      alert(error.response?.data?.detail || "Failed to enroll in ministry");
    }
  };
  

  // Bible verse fetcher
  const fetchBibleVerse = async (reference) => {
    setLoadingVerse(true);
    setShowBibleModal(true);
    setBibleVerseData(null);
    
    try {
      const response = await axios.get(`${API}/bible/kjv/${encodeURIComponent(reference)}`);
      setBibleVerseData(response.data);
    } catch (error) {
      console.error('Error fetching Bible verse:', error);
      setBibleVerseData({
        reference: reference,
        text: 'Unable to load verse. Please try again.',
        translation: 'KJV',
        error: true
      });
    } finally {
      setLoadingVerse(false);
    }
  };


  const loadMinistryEnrollments = async (ministryId) => {
    try {
      const response = await axios.get(`${API}/ministry-enrollments?ministry_id=${ministryId}`);
      setMinistryEnrollments(response.data);
      setShowEnrollmentsModal(true);
    } catch (error) {
      console.error("Error loading ministry enrollments:", error);
      alert("Failed to load enrollments");
    }
  };


  return (
    <div className="relative space-y-3 sm:space-y-4">
      {/* Ambient page-level decorative orbs (behind everything) */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-violet-200/30 rounded-full blur-3xl" />
        <div className="absolute top-2/3 -right-24 w-[28rem] h-[28rem] bg-amber-200/25 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-rose-200/25 rounded-full blur-3xl" />
      </div>

      {/* 1. Live Announcements */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 rounded-lg bg-gradient-to-r from-red-500 to-rose-500">
            <Radio className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            Live Announcements
            <motion.span 
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold"
              animate={{ opacity: [1, 0.6, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
              LIVE
            </motion.span>
          </h3>
        </div>
        <LiveAnnouncementsTicker 
          announcements={announcements} 
          onRefresh={refreshAnnouncements}
        />
      </motion.div>

      {/* 3. Weekly Inspiration with Christian Season */}
      <SeasonalInspiration weeklyVerse={weeklyVerse} />

      {/* 4. Service Schedule & Bible Lessons Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <SectionCard icon={Calendar} title="Service Schedule" subtitle="Join us for worship" gradient="sanctuary" delay={0.2}>
          <div className="space-y-3">
            {/* Holy Week Special Services Banner */}
            {serviceSchedules.some(s => s.is_special) && (() => {
              const specialServices = serviceSchedules.filter(s => s.is_special);
              // Group by day
              const grouped = specialServices.reduce((acc, svc) => {
                if (!acc[svc.day]) acc[svc.day] = [];
                acc[svc.day].push(svc);
                return acc;
              }, {});
              return (
                <div className="mb-4 rounded-2xl overflow-hidden border-2 border-purple-300 shadow-lg" data-testid="holy-week-banner">
                  <div className="bg-gradient-to-r from-purple-700 via-indigo-700 to-purple-800 px-4 py-3 text-white flex items-center gap-2">
                    <Church className="w-5 h-5 text-amber-300" />
                    <h4 className="font-bold text-base tracking-wide">Holy Week Special Services</h4>
                    <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">This Week Only</span>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-3">
                    <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                      {Object.entries(grouped).map(([day, services]) => (
                        <div key={day} className="bg-white rounded-xl border border-purple-200 p-2.5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Calendar className="w-3.5 h-3.5 text-purple-600" />
                            <span className="text-xs font-bold text-purple-800 uppercase tracking-wider">{services[0].name.includes('Good Friday') ? 'Good Friday' : day === 'Saturday' ? 'Holy Saturday' : services[0].name}</span>
                          </div>
                          <div className="space-y-1.5">
                            {services.map((svc, idx) => (
                              <button
                                key={idx}
                                onClick={() => fetchBibleVerse(svc.bible_lesson)}
                                className="w-full text-left p-2 bg-purple-50/80 rounded-lg hover:bg-purple-100 transition-colors group cursor-pointer"
                                data-testid={`holy-week-lesson-${day.toLowerCase()}-${idx}`}
                              >
                                <div className="flex items-center gap-2">
                                  <Clock className="w-3 h-3 text-purple-500 flex-shrink-0" />
                                  <span className="text-xs font-semibold text-slate-700">{svc.time}</span>
                                </div>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <BookOpenText className="w-3 h-3 text-indigo-500 flex-shrink-0" />
                                  <span className="text-xs font-medium text-indigo-700 group-hover:text-indigo-900 truncate">{svc.bible_lesson}</span>
                                  <span className="text-[10px] text-purple-500 ml-auto whitespace-nowrap group-hover:text-purple-700">Read</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {serviceSchedules.filter(s => !s.is_special).length === 0 ? (
                <div className="col-span-full text-center py-6 text-slate-500">
                  <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No services scheduled yet</p>
                </div>
              ) : (
                serviceSchedules.filter(s => !s.is_special).map((schedule, index) => {
                  // Find Bible lessons for this service
                  const serviceLessons = bibleLessons.filter(lesson => {
                    // Match by service type (normalize names)
                    const scheduleType = schedule.name.toLowerCase();
                    const lessonType = lesson.service_type.toLowerCase();
                    
                    // Handle various naming patterns
                    if (scheduleType.includes('sunday') && scheduleType.includes('morning') && lessonType.includes('sunday') && lessonType.includes('morning')) {
                      return true;
                    }
                    if (scheduleType.includes('sunday') && scheduleType.includes('evening') && lessonType.includes('sunday') && lessonType.includes('evening')) {
                      return true;
                    }
                    if (scheduleType.includes('wednesday') && scheduleType.includes('morning') && lessonType.includes('wednesday') && lessonType.includes('morning')) {
                      return true;
                    }
                    if (scheduleType.includes('wednesday') && !scheduleType.includes('morning') && lessonType.includes('wednesday') && !lessonType.includes('morning') && !lessonType.includes('sunday')) {
                      return true;
                    }
                    if (scheduleType.includes('friday') && lessonType.includes('friday')) {
                      return true;
                    }
                    if (scheduleType.includes('new moon') && lessonType.includes('new moon')) {
                      return true;
                    }
                    if (scheduleType.includes('prophets') && lessonType.includes('prophets')) {
                      return true;
                    }
                    return false;
                  }).sort((a, b) => a.lesson_number - b.lesson_number);
                  
                  // Different gradient colors for each service
                  const gradients = [
                    { from: 'from-blue-500', to: 'to-cyan-500', bg: 'from-blue-50', bgTo: 'to-cyan-50', border: 'border-blue-300', text: 'text-blue-900', icon: 'text-blue-600' },
                    { from: 'from-purple-500', to: 'to-pink-500', bg: 'from-purple-50', bgTo: 'to-pink-50', border: 'border-purple-300', text: 'text-purple-900', icon: 'text-purple-600' },
                    { from: 'from-orange-500', to: 'to-red-500', bg: 'from-orange-50', bgTo: 'to-red-50', border: 'border-orange-300', text: 'text-orange-900', icon: 'text-orange-600' },
                    { from: 'from-emerald-500', to: 'to-teal-500', bg: 'from-emerald-50', bgTo: 'to-teal-50', border: 'border-emerald-300', text: 'text-emerald-900', icon: 'text-emerald-600' },
                    { from: 'from-indigo-500', to: 'to-blue-500', bg: 'from-indigo-50', bgTo: 'to-blue-50', border: 'border-indigo-300', text: 'text-indigo-900', icon: 'text-indigo-600' },
                    { from: 'from-rose-500', to: 'to-pink-500', bg: 'from-rose-50', bgTo: 'to-pink-50', border: 'border-rose-300', text: 'text-rose-900', icon: 'text-rose-600' },
                  ];
                  const gradient = gradients[index % gradients.length];
                  
                  return (
                    <motion.div
                      key={schedule.id}
                      className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border ${gradient.border}`}
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ delay: index * 0.05, type: "spring" }}
                      whileHover={{ scale: 1.02, y: -3 }}
                    >
                      {/* Compact Gradient Header */}
                      <div className={`bg-gradient-to-r ${gradient.from} ${gradient.to} p-4 text-white relative overflow-hidden`}>
                        <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
                        <div className="relative z-10">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Calendar className="w-4 h-4 opacity-80" />
                                <span className="text-xs font-semibold uppercase tracking-wider opacity-90">{schedule.day}</span>
                              </div>
                              <h4 className="font-bold text-lg leading-tight">{schedule.name}</h4>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-1 text-white/90">
                                <Clock className="w-3 h-3" />
                                <p className="font-semibold text-sm">{schedule.time}</p>
                              </div>
                            </div>
                          </div>
                          {schedule.description && (
                            <p className="text-white/70 text-xs mt-1 line-clamp-1">{schedule.description}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Compact Card Body */}
                      <div className={`bg-gradient-to-br ${gradient.bg} ${gradient.bgTo} p-3`}>
                        {/* Bible Lessons from scraped data */}
                        {schedule.bible_lesson && schedule.bible_lesson.trim() !== '' ? (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <BookOpenText className={`w-4 h-4 ${gradient.icon}`} />
                              <span className={`text-xs font-bold ${gradient.text} uppercase tracking-wide`}>
                                {schedule.name === 'Bible Study' ? "This Week's Study" : "This Week's Lessons"}
                              </span>
                              {schedule.lesson_date && (
                                <span className="text-xs text-slate-500 ml-auto">
                                  {(() => {
                                    try {
                                      const d = new Date(schedule.lesson_date + 'T00:00:00');
                                      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                                    } catch { return schedule.lesson_date; }
                                  })()}
                                </span>
                              )}
                            </div>
                            {/* Show Topic for Bible Study */}
                            {schedule.lesson_topic && (
                              <div className="mb-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-xs text-amber-800 font-semibold">
                                  Topic: {schedule.lesson_topic}
                                </p>
                              </div>
                            )}
                            <div className="space-y-2">
                              {schedule.bible_lesson.split(',').map((lesson, lessonIdx) => {
                                const trimmedLesson = lesson.trim();
                                return (
                                  <button
                                    key={lessonIdx}
                                    onClick={() => fetchBibleVerse(trimmedLesson)}
                                    className="w-full text-left p-2.5 bg-white rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all duration-200 cursor-pointer group"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="p-1.5 rounded-md bg-gradient-to-r from-blue-500 to-purple-500 group-hover:scale-110 transition-transform">
                                        <BookOpenText className="w-3 h-3 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`font-semibold ${gradient.text} text-sm truncate`}>
                                          {schedule.bible_lesson.includes(',') ? 
                                            `${lessonIdx === 0 ? '1st' : '2nd'}: ` : ''}
                                          {trimmedLesson}
                                        </p>
                                        <p className="text-xs text-blue-600 font-medium group-hover:text-blue-700">
                                          Click to read →
                                        </p>
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : serviceLessons.length > 0 ? (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <BookOpenText className={`w-4 h-4 ${gradient.icon}`} />
                              <span className={`text-xs font-bold ${gradient.text} uppercase tracking-wide`}>This Week's Lessons</span>
                            </div>
                            <div className="space-y-2">
                              {serviceLessons.map((lesson) => (
                                <button
                                  key={lesson.id}
                                  onClick={() => fetchBibleVerse(lesson.full_reference)}
                                  className="w-full text-left p-2.5 bg-white rounded-lg border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all duration-200 cursor-pointer group"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded-md bg-gradient-to-r from-blue-500 to-purple-500 group-hover:scale-110 transition-transform">
                                      <BookOpenText className="w-3 h-3 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`font-semibold ${gradient.text} text-sm truncate`}>
                                        {serviceLessons.length > 1 ? 
                                          `${lesson.lesson_number === 1 ? '1st' : '2nd'}: ` : ''}
                                        {lesson.full_reference}
                                      </p>
                                      <p className="text-xs text-blue-600 font-medium group-hover:text-blue-700">
                                        Click to read →
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-3">
                            <div className={`w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-r ${gradient.from} ${gradient.to} flex items-center justify-center shadow-md`}>
                              <Church className="w-5 h-5 text-white" />
                            </div>
                            <p className={`font-semibold ${gradient.text} text-sm`}>Join Us in Worship</p>
                            <p className="text-slate-500 text-xs mt-1">
                              {schedule.description || `Every ${schedule.day}`}
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>
        </SectionCard>
      </motion.div>

      {/* 5. Ministries, Flyers & Member Registration - Full Width Layout */}
      <div className="space-y-4 sm:space-y-6">
        {/* Vineyard Events - Vibrant Banner with Decorative Background */}
        {flyers && flyers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative overflow-hidden rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl bg-gradient-to-br from-violet-100 via-amber-50 to-rose-100 border border-violet-200/60"
            data-testid="vineyard-events-section"
          >
            {/* Decorative orbs — brand-aligned (violet + amber + rose) */}
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-violet-300/30 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-rose-300/30 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-1/2 left-1/3 w-40 h-40 bg-amber-300/25 rounded-full blur-2xl pointer-events-none" />

            {/* Floating sparkles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={`sparkle-${i}`}
                  className="absolute w-1.5 h-1.5 rounded-full bg-violet-400/60"
                  style={{ left: `${10 + i * 11}%`, top: `${15 + (i % 4) * 20}%` }}
                  animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.4, 0.8] }}
                  transition={{ duration: 2 + i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
                />
              ))}
            </div>

            {/* Header with animated icon + ribbon tagline */}
            <div className="relative flex flex-wrap items-center gap-3 mb-4">
              <motion.div
                className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-br from-violet-600 via-purple-600 to-amber-500 shadow-lg"
                animate={{ rotate: [0, -3, 3, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </motion.div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-2xl font-extrabold bg-gradient-to-r from-violet-700 via-purple-700 to-amber-600 bg-clip-text text-transparent">
                  Vineyard Events
                </h3>
                <p className="text-violet-800/70 text-xs sm:text-sm font-medium">
                  ✨ Don't miss out · Join us · Be blessed
                </p>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500 text-white text-xs font-bold shadow animate-pulse">
                <span className="w-2 h-2 rounded-full bg-white" />
                LIVE
              </span>
            </div>

            <div className={`relative grid gap-3 sm:gap-4 justify-center ${
              flyers.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' :
              flyers.length === 2 ? 'grid-cols-2 max-w-2xl mx-auto' :
              flyers.length === 3 ? 'grid-cols-2 md:grid-cols-3 max-w-4xl mx-auto' :
              'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
            }`}>
              {flyers.slice(0, 4).map((flyer, index) => (
                <LongPressShare
                  key={flyer.id}
                  kind="flyer"
                  itemId={flyer.id}
                  title={flyer.title}
                  description={flyer.description}
                >
                <motion.div
                  className="block group cursor-pointer"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, type: 'spring', stiffness: 100 }}
                  whileHover={{ scale: 1.04, y: -6 }}
                  onClick={() => {
                    setSelectedFlyer(flyer);
                    setShowFlyerModal(true);
                  }}
                  data-testid={`vineyard-event-card-${flyer.id}`}
                >
                  <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-violet-200 group-hover:border-violet-500 group-hover:shadow-2xl group-hover:shadow-violet-500/40 transition-all aspect-[3/4]">
                    <img
                      src={flyer.image_url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${flyer.image_url}` : flyer.image_url}
                      alt={flyer.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?w=800&auto=format&fit=crop';
                      }}
                    />

                    {/* Top-left date pill (if event_date present) */}
                    {flyer.event_date && (
                      <div className="absolute top-2 left-2 sm:top-3 sm:left-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm shadow text-[10px] sm:text-xs font-bold text-amber-700 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {(() => {
                          try {
                            return new Date(flyer.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                          } catch { return 'Soon'; }
                        })()}
                      </div>
                    )}

                    {/* Top-right pulsing eye badge — long-press the card to share */}
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10">
                      <motion.div
                        className="bg-gradient-to-br from-violet-600 to-amber-500 backdrop-blur-sm rounded-full p-1.5 sm:p-2 shadow-lg ring-2 ring-white/70"
                        animate={{ scale: [1, 1.12, 1] }}
                        transition={{ duration: 1.8, repeat: Infinity }}
                      >
                        <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                      </motion.div>
                    </div>

                    {/* Subtle hover prompt — only visible while hovering (desktop)
                        or never (mobile). Keeps the flyer art completely clean
                        until the user opens it. */}
                    <div className="absolute inset-x-0 bottom-0 px-3 py-2 flex items-center justify-center
                                    bg-gradient-to-t from-black/70 to-transparent
                                    opacity-0 group-hover:opacity-100 transition-opacity duration-300
                                    pointer-events-none">
                      <span className="text-amber-200 text-[11px] sm:text-xs font-bold tracking-wide drop-shadow">
                        Tap to view full flyer →
                      </span>
                    </div>
                  </div>
                </motion.div>
                </LongPressShare>
              ))}
            </div>
          </motion.div>
        )}

        {/* Ministries & Member Registration - Side by Side */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2">
          {/* Left - Get Involved */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative overflow-hidden rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl bg-gradient-to-br from-violet-50 via-purple-50/70 to-rose-50/40 border border-violet-200/60"
          >
            {/* Decorative orbs */}
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-violet-300/25 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-16 w-56 h-56 bg-rose-300/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
              <motion.div
                className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br from-violet-500 via-purple-600 to-rose-500 shadow-lg"
                animate={{ rotate: [0, -4, 4, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Handshake className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </motion.div>
              <div>
                <h2 className="text-lg sm:text-xl font-extrabold bg-gradient-to-r from-violet-700 via-purple-700 to-rose-600 bg-clip-text text-transparent">Get Involved</h2>
                <p className="text-violet-800/70 text-xs sm:text-sm font-medium">Join our ministries</p>
              </div>
            </div>

            <p className="text-slate-600 text-xs sm:text-sm mb-3 sm:mb-4">There's a place for you to serve and make a lasting impact.</p>
            
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-purple-300 scrollbar-track-transparent">
              {ministries.length === 0 ? (
                <div className="text-center py-6">
                  <Handshake className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No ministries available yet</p>
                </div>
              ) : (
                ministries.map((ministry, index) => (
                  <motion.div
                    key={ministry.id}
                    className="group relative overflow-hidden p-4 rounded-xl border-2 bg-white/80 backdrop-blur-sm border-violet-200 hover:border-violet-400 hover:shadow-lg hover:shadow-violet-200/40 transition-all cursor-pointer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => {
                      setSelectedMinistry(ministry);
                      setEnrollmentForm(prev => ({ ...prev, ministry_id: ministry.id }));
                      setShowEnrollmentModal(true);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500 via-purple-600 to-rose-500 group-hover:scale-110 transition-transform">
                        <HeartHandshake className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-violet-900 mb-1">{ministry.name}</h4>
                        <p className="text-slate-600 text-sm line-clamp-2">{ministry.description}</p>
                        <span className="inline-flex items-center gap-1 mt-2 text-violet-700 text-xs font-medium">
                          <Send className="w-3 h-3" />
                          Click to Join
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>

          {/* Right - Member Registration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="relative overflow-hidden rounded-3xl p-6 shadow-xl flex flex-col bg-gradient-to-br from-amber-50 via-yellow-50/70 to-purple-50/50 border border-amber-200/60"
          >
            {/* Decorative parchment corners */}
            <div className="absolute -top-12 -right-12 w-44 h-44 bg-amber-300/25 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-16 -left-12 w-48 h-48 bg-purple-300/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative flex items-center gap-4 mb-5">
              <motion.div
                className="p-3 rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-violet-600 shadow-lg"
                whileHover={{ scale: 1.08, rotate: 6 }}
              >
                <User className="w-7 h-7 text-white" />
              </motion.div>
              <div>
                <h3 className="text-xl font-extrabold bg-gradient-to-r from-amber-700 via-orange-600 to-violet-700 bg-clip-text text-transparent">Member Registration</h3>
                <p className="text-amber-900/70 text-sm font-medium">Celestial Church of Christ — Valley of Berachah</p>
              </div>
            </div>
            
            {registrationSuccess ? (
              <motion.div 
                className="flex-1 flex flex-col justify-center"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
              >
                <div className="flex flex-col items-center gap-4 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center">
                    <h4 className="font-bold text-emerald-700 text-lg mb-1">God bless you for joining us</h4>
                    <p className="text-emerald-600 text-sm">We are glad to have you.</p>
                  </div>
                </div>
              </motion.div>
            ) : memberId && !showMemberRegistration ? (
              <motion.div 
                className="flex-1 flex flex-col justify-center"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
              >
                <div className="flex flex-col items-center gap-4 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center">
                    <h4 className="font-bold text-emerald-700 text-lg mb-1">You're Registered!</h4>
                    <p className="text-emerald-600 text-sm">GPS attendance is active on Sundays</p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="flex-1 flex flex-col">
                {!showMemberRegistration ? (
                  <div>
                    <p className="text-slate-600 text-sm mb-4">Join our church family by registering below. All fields are required.</p>
                    <motion.button
                      onClick={() => setShowMemberRegistration(true)}
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg flex items-center justify-center gap-2 mt-auto"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="homepage-register-btn"
                    >
                      <Users className="w-5 h-5" />
                      <span>Register as Member</span>
                    </motion.button>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-3"
                  >
                    {registrationError && (
                      <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                        <p className="text-red-600 text-sm" data-testid="homepage-reg-error">{registrationError}</p>
                      </div>
                    )}
                    
                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Enter your full name"
                          value={registrationData.full_name}
                          onChange={e => setRegistrationData(prev => ({ ...prev, full_name: e.target.value }))}
                          data-testid="homepage-reg-name"
                          className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="tel"
                          placeholder="e.g. 07XXX XXXXXX"
                          value={registrationData.phone}
                          onChange={e => setRegistrationData(prev => ({ ...prev, phone: e.target.value }))}
                          data-testid="homepage-reg-phone"
                          className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="email"
                          placeholder="Enter your email"
                          value={registrationData.email}
                          onChange={e => setRegistrationData(prev => ({ ...prev, email: e.target.value }))}
                          data-testid="homepage-reg-email"
                          className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm"
                        />
                      </div>
                    </div>

                    {/* Birthday */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Birthday *</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <select
                            value={registrationData.birthday_month}
                            onChange={e => setRegistrationData(prev => ({ ...prev, birthday_month: e.target.value }))}
                            data-testid="homepage-reg-birthday-month"
                            className="w-full pl-10 pr-8 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm appearance-none bg-white"
                          >
                            <option value="">Month</option>
                            {MONTHS_LIST.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          placeholder="Day"
                          value={registrationData.birthday_day}
                          onChange={e => setRegistrationData(prev => ({ ...prev, birthday_day: e.target.value }))}
                          data-testid="homepage-reg-birthday-day"
                          className="w-20 px-3 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-400 outline-none text-sm text-center"
                        />
                      </div>
                    </div>

                    {/* Gender */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Gender *</label>
                      <div className="flex gap-2">
                        {['Male', 'Female'].map(g => (
                          <button
                            key={g}
                            type="button"
                            onClick={() => setRegistrationData(prev => ({ ...prev, gender: g }))}
                            data-testid={`homepage-reg-gender-${g.toLowerCase()}`}
                            className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all ${
                              registrationData.gender === g
                                ? 'bg-blue-500 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => setShowMemberRegistration(false)}
                        className="flex-1 py-3 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRegistration}
                        data-testid="homepage-reg-submit"
                        className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg flex items-center justify-center gap-2"
                      >
                        <span>Register</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* 6. Connect With Us Section - Full Width */}
      <ConnectWithUsSection
        form={contactForm}
        onFormChange={(patch) => setContactForm(prev => ({ ...prev, ...patch }))}
        onSubmit={submitContactForm}
      />

      {/* Ministry Enrollment Modal */}
      <MinistryEnrollmentModal
        open={showEnrollmentModal}
        ministry={selectedMinistry}
        form={enrollmentForm}
        onFormChange={(patch) => setEnrollmentForm(prev => ({ ...prev, ...patch }))}
        onSubmit={enrollInMinistry}
        onClose={() => {
          setShowEnrollmentModal(false);
          setSelectedMinistry(null);
          setEnrollmentForm({ ministry_id: "", member_name: "", member_phone: "", member_email: "" });
        }}
      />

      {/* Ministry Enrollments Modal (Admin) */}
      {isAdmin && (
        <MinistryEnrollmentsAdminModal
          open={showEnrollmentsModal}
          enrollments={ministryEnrollments}
          onClose={() => {
            setShowEnrollmentsModal(false);
            setMinistryEnrollments([]);
          }}
        />
      )}


      {/* Bible Verse Modal */}
      <BibleVerseModal
        open={showBibleModal}
        onClose={() => { setShowBibleModal(false); setBibleVerseData(null); }}
        verseData={bibleVerseData}
        loading={loadingVerse}
      />

      {/* Flyer Lightbox Modal */}
      <FlyerLightboxModal
        open={showFlyerModal}
        flyer={selectedFlyer}
        onClose={() => setShowFlyerModal(false)}
      />



      {/* About Us Modal */}
      <AboutUsModal
        open={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />

    </div>
  );
}


// OLD ATTENDANCE PAGE - REPLACED WITH GPS ATTENDANCE SYSTEM
// Kept for reference, can be deleted later
/*
function AttendancePage({ attendance, setAttendance }) {
  const [draft, setDraft] = useState({
    date: new Date().toISOString().slice(0,10),
    service: "Sunday Service",
    adults: 0,
    children: 0,
    first_timers: 0,
    volunteers: 0,
    notes: "",
  });

  const total = (r) => (r.adults||0) + (r.children||0) + (r.first_timers||0) + (r.volunteers||0);

  const add = async () => {
    try {
      const response = await axios.post(`${API}/attendance`, {
        date: draft.date,
        service: draft.service,
        adults: draft.adults,
        children: draft.children,
        first_timers: draft.first_timers,
        volunteers: draft.volunteers,
        notes: draft.notes
      });
      setAttendance(a => [response.data, ...a]);
    } catch (error) {
      console.error("Error adding attendance:", error);
      setAttendance(a => [{ id: Date.now().toString(), ...draft }, ...a]);
    }
  };

  const toCSV = () => {
    // This function is only called from admin context, but double-check for security
    const header = ["date","service","adults","children","firstTimers","volunteers","total","notes"];
    const rows = attendance.map(r => [r.date, r.service, r.adults, r.children, r.first_timers, r.volunteers, total(r), (r.notes||"").replace(/\n/g, " ")]);
    const csv = [header, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <SectionCard icon={BarChart3} title="Attendance Census" subtitle="Recording God's blessings" gradient="info">
        <div className="space-y-6">
          <div className="grid md:grid-cols-6 gap-3">
            <input className="border-2 border-blue-200 rounded-2xl px-4 py-3 md:col-span-2 focus:border-blue-400 transition-colors" type="date" value={draft.date} onChange={e=>setDraft(d=>({...d, date: e.target.value}))} />
            <input className="border-2 border-blue-200 rounded-2xl px-4 py-3 md:col-span-2 focus:border-blue-400 transition-colors" placeholder="Service (e.g., Sunday 10am)" value={draft.service} onChange={e=>setDraft(d=>({...d, service: e.target.value}))} />
            <FancyButton onClick={add} variant="info" className="md:col-span-2">
              Save Record
            </FancyButton>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {([
              ["Adults", "adults", "from-blue-500 to-cyan-500"],
              ["Children", "children", "from-green-500 to-teal-500"],
              ["First-timers", "first_timers", "from-purple-500 to-pink-500"],
              ["Volunteers", "volunteers", "from-yellow-500 to-orange-500"],
            ]).map(([label, key, gradient], index) => (
              <motion.div 
                key={key} 
                className="p-4 rounded-2xl bg-white border-2 border-blue-100 hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="text-center mb-3">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-r ${gradient} mb-2`}>
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-800">{label}</h4>
                </div>
                <div className="flex items-center justify-between">
                  <motion.button 
                    onClick={()=>setDraft(d=>({...d, [key]: Math.max(0,(d[key]||0)-1)}))}
                    className="w-8 h-8 rounded-xl bg-red-100 text-red-600 hover:bg-red-200 transition-colors flex items-center justify-center font-bold"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    −
                  </motion.button>
                  <motion.span 
                    className="text-2xl font-bold text-slate-800 min-w-[3rem] text-center"
                    key={draft[key]}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", bounce: 0.6 }}
                  >
                    {draft[key]||0}
                  </motion.span>
                  <motion.button 
                    onClick={()=>setDraft(d=>({...d, [key]: (d[key]||0)+1}))}
                    className="w-8 h-8 rounded-xl bg-green-100 text-green-600 hover:bg-green-200 transition-colors flex items-center justify-center font-bold"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    +
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
          
          <textarea className="border-2 border-blue-200 rounded-2xl px-4 py-3 w-full resize-none focus:border-blue-400 transition-colors" rows={2} placeholder="Notes (optional)" value={draft.notes} onChange={e=>setDraft(d=>({...d, notes: e.target.value}))} />
          
          <div className="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100">
            <div className="text-slate-800">
              <span className="text-sm">Total Attendance: </span>
              <motion.span 
                className="text-2xl font-bold text-blue-600"
                key={total(draft)}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
              >
                {total(draft)}
              </motion.span>
            </div>
            <FancyButton onClick={toCSV} variant="success" size="sm">
              📊 Export CSV
            </FancyButton>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={Calendar} title="Recent Records" subtitle="Latest attendance entries" gradient="success">
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {attendance.slice(0,10).map((r, index) => (
            <motion.div 
              key={r.id} 
              className="p-4 rounded-2xl bg-gradient-to-r from-green-50 to-teal-50 border border-green-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-slate-800">{r.date} • {r.service}</h4>
                <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                  Total: {total(r)}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs text-slate-600">
                <span>Adults: <strong>{r.adults||0}</strong></span>
                <span>Children: <strong>{r.children||0}</strong></span>
                <span>First-timers: <strong>{r.first_timers||0}</strong></span>
                <span>Volunteers: <strong>{r.volunteers||0}</strong></span>
              </div>
              {r.notes && <p className="text-xs text-slate-500 mt-2 italic">{r.notes}</p>}
            </motion.div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
*/

// --- Utils ---
function formatDateRange(startISO, endISO) {
  try {
    const start = new Date(startISO);
    const end = endISO ? new Date(endISO) : null;
    const d = start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const t = start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    if (!end) return `${d} • ${t}`;
    const t2 = end.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return `${d} • ${t}–${t2}`;
  } catch {
    return startISO;
  }
}

function nextDateForWeekday(weekdayIndex) {
  const today = new Date();
  const currentDay = today.getDay();
  const daysUntilNext = ((weekdayIndex - currentDay) + 7) % 7;
  const nextDate = new Date(today.getTime() + (daysUntilNext * 24 * 60 * 60 * 1000));
  return nextDate.toISOString().slice(0, 10);
}

function makeLocalDateTimeISO(dateStr, timeHHMM) {
  return `${dateStr}T${timeHHMM}:00`;
}

function addHoursISO(datetimeISO, hours) {
  const date = new Date(datetimeISO);
  date.setHours(date.getHours() + hours);
  return date.toISOString().slice(0, 16);
}

// Webcast Page Component - Displays content from Admin Panel
function WebcastPage() {
  const [webcastItems, setWebcastItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const loadWebcast = async () => {
      try {
        const response = await axios.get(`${API}/webcast`);
        // Filter to only show active items
        const activeItems = response.data.filter(item => item.is_active);
        setWebcastItems(activeItems);
      } catch (error) {
        console.error('Error loading webcast:', error);
      }
      setLoading(false);
    };
    loadWebcast();
  }, []);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([^&\n?#]+)/);
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }
    return '';
  };

  const liveStreams = webcastItems.filter(item => item.item_type === 'live');
  const videos = webcastItems.filter(item => item.item_type === 'recorded');
  const photos = webcastItems.filter(item => item.item_type === 'photo');

  const filteredItems = activeFilter === 'all' 
    ? webcastItems 
    : webcastItems.filter(item => item.item_type === activeFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section with Live Stream */}
      {liveStreams.some(s => s.is_live_now) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-red-600 to-rose-600 p-1"
        >
          <div className="bg-slate-900 rounded-[22px] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-500 text-white text-sm font-medium animate-pulse">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                LIVE NOW
              </div>
              <h2 className="text-xl font-bold text-white">
                {liveStreams.find(s => s.is_live_now)?.title || 'Live Service'}
              </h2>
            </div>
            <div className="aspect-video rounded-xl overflow-hidden">
              <iframe
                src={getYouTubeEmbedUrl(liveStreams.find(s => s.is_live_now)?.youtube_url)}
                className="w-full h-full"
                allowFullScreen
                title="Live Stream"
              />
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Content */}
      <SectionCard 
        icon={Video} 
        title="Church Webcast" 
        subtitle="Watch live services, recorded sermons, and view our photo gallery" 
        gradient="royal"
      >
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'all', label: 'All', count: webcastItems.length },
            { id: 'live', label: 'Live Streams', count: liveStreams.length },
            { id: 'recorded', label: 'Videos', count: videos.length },
            { id: 'photo', label: 'Photos', count: photos.length }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeFilter === filter.id
                  ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>

        {webcastItems.length === 0 ? (
          <div className="text-center py-16">
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-red-100 to-rose-100 mb-6"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ repeat: Infinity, duration: 3 }}
            >
              <Video className="w-10 h-10 text-red-500" />
            </motion.div>
            <h3 className="text-xl font-semibold text-slate-600 mb-2">No Content Yet</h3>
            <p className="text-slate-500">Check back soon for live streams, videos, and photos!</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Videos Section */}
            {(activeFilter === 'all' || activeFilter === 'live' || activeFilter === 'recorded') && 
              filteredItems.filter(i => i.item_type === 'live' || i.item_type === 'recorded').length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Video className="w-5 h-5 text-red-500" />
                  Videos & Live Streams
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredItems
                    .filter(item => item.item_type === 'live' || item.item_type === 'recorded')
                    .map((item, index) => (
                      <LongPressShare
                        key={item.id}
                        kind="media"
                        itemId={item.id}
                        title={item.title}
                        description={item.description}
                      >
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100"
                      >
                        <div className="aspect-video relative">
                          <iframe
                            src={getYouTubeEmbedUrl(item.youtube_url)}
                            className="w-full h-full"
                            allowFullScreen
                            title={item.title}
                          />
                          {item.is_live_now && (
                            <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-red-500 text-white text-sm font-medium animate-pulse flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                              LIVE
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h4 className="font-bold text-slate-800 mb-1">{item.title}</h4>
                          {item.description && (
                            <p className="text-sm text-slate-600 line-clamp-2">{item.description}</p>
                          )}
                        </div>
                      </motion.div>
                      </LongPressShare>
                    ))}
                </div>
              </div>
            )}

            {/* Photos Section */}
            {(activeFilter === 'all' || activeFilter === 'photo') && 
              filteredItems.filter(i => i.item_type === 'photo').length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-blue-500" />
                  Photo Gallery
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredItems
                    .filter(item => item.item_type === 'photo')
                    .map((item, index) => (
                      <LongPressShare
                        key={item.id}
                        kind="media"
                        itemId={item.id}
                        title={item.title}
                        description={item.description}
                      >
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow-lg"
                        onClick={() => setSelectedImage(item)}
                        data-testid={`photo-gallery-${item.id}`}
                      >
                        <img
                          src={item.photo_url}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        {/* Subtle desktop-hover-only prompt — no title/description overlay
                            so the photograph speaks for itself until tapped. */}
                        <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 flex items-center justify-center
                                        bg-gradient-to-t from-black/65 to-transparent
                                        opacity-0 group-hover:opacity-100 transition-opacity duration-300
                                        pointer-events-none">
                          <span className="text-amber-200 text-[10px] font-bold tracking-wide drop-shadow">
                            Tap to view →
                          </span>
                        </div>
                      </motion.div>
                      </LongPressShare>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm" 
              onClick={() => setSelectedImage(null)}
            />
            <motion.div
              className="relative bg-white rounded-3xl overflow-hidden shadow-2xl max-w-4xl max-h-[90vh] w-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <div className="relative">
                <img
                  src={selectedImage.photo_url}
                  alt={selectedImage.title}
                  className="w-full max-h-[70vh] object-contain"
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-800 mb-2">{selectedImage.title}</h3>
                {selectedImage.description && (
                  <p className="text-slate-600">{selectedImage.description}</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Main App with Routing
export default function App() {
  return (
    <AppProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/meet-the-team" element={<MeetOurTeamPage />} />
          <Route path="/choir-portal" element={<ChoirMemberPortal />} />
          <Route path="/rules-presentation" element={<RulesPresentation />} />
          <Route path="/media/live-display" element={<LiveDisplay />} />
          <Route path="/remote" element={<RemoteControl />} />
          <Route path="/presentation-remote" element={<PresentationRemote />} />
          <Route path="/member-register" element={<MemberRegister />} />
          <Route path="/volunteer-clockin" element={<VolunteerClockIn />} />
          <Route path="/harvest-record" element={<MobileHarvestRecord />} />
          <Route path="/choir-checkin" element={<ChoirCheckIn />} />
          <Route path="/member-login" element={<MemberLogin />} />
          <Route path="/minister-availability" element={<MinisterAvailability />} />
          <Route path="/flyer/:id" element={<FlyerDetailPage />} />
          <Route path="/gallery/:id" element={<MediaDetailPage />} />
          <Route path="/*" element={<ChurchAppContent />} />
        </Routes>
      </Router>
    </AppProvider>
  );
}
